import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import NoteCard from './NoteCard';
import SafeIcon from '../common/SafeIcon';

export const BoardView = ({ notes }) => {
  const { selectedNoteIds, setSelectedNoteIds } = useStore();
  const [activeSwipeId, setActiveSwipeId] = useState(null);

  if (notes.length === 0) return <EmptyState />;
  
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-6 space-y-4 md:space-y-6 pb-20">
      {notes.map(note => (
        <div key={note.id} className="relative group break-inside-avoid">
          {/* Multi-select indicator (Desktop only) */}
          <div 
            className={`absolute -top-1 -left-1 z-20 w-6 h-6 rounded-full border-2 border-white shadow-md transition-all cursor-pointer hidden md:flex items-center justify-center ${selectedNoteIds.includes(note.id) ? 'bg-blue-500 opacity-100' : 'bg-gray-200 opacity-0 group-hover:opacity-100'}`} 
            onClick={(e) => { 
              e.stopPropagation(); 
              setSelectedNoteIds(prev => prev.includes(note.id) ? prev.filter(id => id !== note.id) : [...prev, note.id]); 
            }} 
          >
            {selectedNoteIds.includes(note.id) && <SafeIcon name="Check" className="text-white text-xs" />}
          </div>
          <NoteCard 
            note={note} 
            activeSwipeId={activeSwipeId} 
            setActiveSwipeId={setActiveSwipeId} 
          />
        </div>
      ))}
    </div>
  );
};

export const ListView = ({ notes }) => {
  const { setEditingNote, togglePin, selectedNoteIds, setSelectedNoteIds } = useStore();
  const [activeSwipeId, setActiveSwipeId] = useState(null);

  if (notes.length === 0) return <EmptyState />;

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedNoteIds(prev => prev.includes(id) ? prev.filter(iid => iid !== id) : [...prev, id]);
  };

  // On very small mobile, switch to swipeable cards even in List view for consistency
  const viewportW = window.innerWidth;
  if (viewportW < 840) {
    return (
      <div className="space-y-3 sm:space-y-4 pb-20">
        {notes.map(note => (
          <NoteCard 
            key={note.id} 
            note={note} 
            activeSwipeId={activeSwipeId} 
            setActiveSwipeId={setActiveSwipeId} 
          />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-20">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full text-left border-collapse min-w-[600px] min-[840px]:min-w-0">
          <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase text-gray-500 font-bold tracking-widest">
            <tr>
              <th className="p-4 w-12 text-center">
                <input type="checkbox" checked={selectedNoteIds.length === notes.length && notes.length > 0} onChange={(e) => setSelectedNoteIds(e.target.checked ? notes.map(n => n.id) : [])} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4" />
              </th>
              <th className="p-4 w-10"></th>
              <th className="p-4">Title</th>
              <th className="p-4 hidden sm:table-cell">Type</th>
              <th className="p-4">Tags</th>
              <th className="p-4">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {notes.map(note => (
              <tr key={note.id} onClick={() => setEditingNote(note)} className={`hover:bg-gray-50 cursor-pointer transition-colors group ${selectedNoteIds.includes(note.id) ? 'bg-blue-50/50' : ''}`} >
                <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedNoteIds.includes(note.id)} onChange={(e) => toggleSelect(e, note.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4" />
                </td>
                <td className="p-4" onClick={e => e.stopPropagation()}>
                  <button onClick={() => togglePin(note)} className="text-gray-300 hover:text-yellow-500 transition-colors">
                    <SafeIcon name="Star" className={note.pinned ? 'fill-current text-yellow-500' : ''} />
                  </button>
                </td>
                <td className="p-4 font-bold text-gray-900 truncate max-w-[150px] md:max-w-[200px]">{note.title || 'Untitled Note'}</td>
                <td className="p-4 text-gray-500 capitalize hidden sm:table-cell">
                  <div className="flex items-center gap-2">
                    <SafeIcon name={note.type === 'text' ? 'FileText' : note.type === 'checklist' ? 'CheckSquare' : 'Grid'} className="text-gray-400" />
                    {note.type}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex gap-1 flex-wrap">
                    {note.tags?.slice(0, 2).map(t => (
                      <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold">#{t}</span>
                    ))}
                    {note.tags?.length > 2 && <span className="text-[10px] text-gray-400">+{note.tags.length - 2}</span>}
                  </div>
                </td>
                <td className="p-4 text-gray-500 text-xs">{new Date(note.updatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const TimelineView = ({ notes }) => {
  const { setEditingNote } = useStore();
  if (notes.length === 0) return <EmptyState />;
  const sorted = [...notes].sort((a, b) => b.updatedAt - a.updatedAt);
  
  return (
    <div className="max-w-3xl mx-auto py-4 md:py-8 pb-20">
      <div className="relative border-l-2 border-gray-200 ml-4 space-y-6 md:space-y-8">
        {sorted.map(note => (
          <div key={note.id} className="relative pl-6 md:pl-8">
            <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow-sm" />
            <div className="text-[10px] md:text-xs font-bold text-gray-400 mb-1 uppercase tracking-widest">
              {new Date(note.updatedAt).toLocaleString()}
            </div>
            <div onClick={() => setEditingNote(note)} className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" >
              <h4 className="font-bold text-gray-900 mb-1 text-base md:text-lg">{note.title || 'Untitled Note'}</h4>
              <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                {note.type === 'text' ? note.bodyText : `[${note.type} content]`}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-64 text-gray-400">
    <SafeIcon name="Inbox" className="text-4xl mb-4 opacity-50" />
    <p className="font-medium text-sm">No notes found in this view.</p>
  </div>
);